import React, { useEffect, useState } from "react";
import { devPlaygroundEnter } from "../../api/devPlaygroundAuth.js";
import { encrypted, tokenManager } from "../../utils/utility.jsx";
import DeepInsightsChat from "../DeepInsightsChat/DeepInsightsChat.jsx";
import LatencyLab from "./LatencyLab.jsx";
import "./DevPlayground.css";

const STORAGE_KEYS = {
  modelId: "playgroundModelId",
  sttProvider: "playgroundSttProvider",
  sttModelId: "playgroundSttModelId",
  ttsModel: "playgroundTtsModel",
};

// Friendlier display names for the raw provider keys the backend returns.
const PROVIDER_LABELS = {
  elevenlabs: "ElevenLabs",
  gemini: "Gemini",
};

/**
 * Standalone model/engine comparison page — not part of the SSO/RBAC app
 * shell. No login: entering the page surfaces dropdowns that let the
 * tester pick the chat LLM and STT/TTS engines for the session. Those
 * choices are persisted to localStorage and read inline, at the exact call
 * sites that need them, by DeepInsightsChat.jsx and VoiceConversationOverlay.jsx
 * — the same "demo mode" convention DevDemo's welcome message already uses,
 * rather than prop-drilling through the shared chat component tree.
 *
 * Access is gated purely by the backend's /api/dev-playground/enter (which
 * mints the local-only "dev-playground" token, accepted by the backend's
 * ENABLE_DEV_DEMO-gated (or, locally, DEBUG-gated) bypass in
 * get_current_user.py) returning 404 when the feature flag is off. The
 * resulting ugdn_id ("DEV-PLAYGROUND") is what gates every model/engine
 * override server-side — this page never touches conversational_layer
 * memory and is not a persona demo.
 */
function DevPlayground() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [options, setOptions] = useState(null);

  const [modelId, setModelId] = useState("");
  const [sttProvider, setSttProvider] = useState("");
  const [sttModelId, setSttModelId] = useState("");
  const [ttsProvider, setTtsProvider] = useState("");
  const [ttsModel, setTtsModel] = useState("");

  const [showLatencyLab, setShowLatencyLab] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { data, error: enterError } = await devPlaygroundEnter();
    setLoading(false);
    if (!data || enterError) {
      setError("This preview page isn't enabled in this environment.");
      return;
    }

    tokenManager.setToken(data.token, "token");
    localStorage.setItem(
      "LoggedInDetails",
      encrypted.encrypt(
        JSON.stringify({
          user_id: "dev-playground",
          username: "dev-playground",
          full_name: "Playground",
          email: "dev-playground@uplgenie.local",
          role: "playground",
          team: "playground",
          ugdn_id: "DEV-PLAYGROUND",
        })
      )
    );

    setOptions(data);
    setModelId(data.chat_models?.[0]?.model_id || "");

    // Default to each provider's first *live* model (so voice chat streams
    // out of the box), but the dropdowns below still list every model
    // (live + batch) for both providers — batch picks are allowed and just
    // show a warning ticker, they aren't hidden.
    const sttByProvider = data.stt_models_by_provider || {};
    const firstSttProvider = Object.keys(sttByProvider)[0] || "";
    const sttModelsForDefault = sttByProvider[firstSttProvider] || [];
    const firstLiveSttModel = sttModelsForDefault.find((m) => m.kind === "live");
    setSttProvider(firstSttProvider);
    setSttModelId((firstLiveSttModel || sttModelsForDefault[0])?.model_id || "");

    const ttsByProvider = data.tts_models_by_provider || {};
    const firstTtsProvider = Object.keys(ttsByProvider)[0] || "";
    const ttsModelsForDefault = ttsByProvider[firstTtsProvider] || [];
    const firstLiveTtsModel = ttsModelsForDefault.find((m) => m.kind === "live");
    setTtsProvider(firstTtsProvider);
    setTtsModel((firstLiveTtsModel || ttsModelsForDefault[0])?.model_id || "");

    setLoggedIn(true);
  };

  // Keep localStorage (and therefore the chat/voice overlay's inline reads)
  // in sync with the dropdown selections.
  useEffect(() => {
    if (modelId) localStorage.setItem(STORAGE_KEYS.modelId, modelId);
  }, [modelId]);
  useEffect(() => {
    if (sttProvider) localStorage.setItem(STORAGE_KEYS.sttProvider, sttProvider);
  }, [sttProvider]);
  useEffect(() => {
    if (sttModelId) localStorage.setItem(STORAGE_KEYS.sttModelId, sttModelId);
  }, [sttModelId]);
  useEffect(() => {
    if (ttsModel) localStorage.setItem(STORAGE_KEYS.ttsModel, ttsModel);
  }, [ttsModel]);

  // Every provider and every model (live + batch) is listed here — ElevenLabs'
  // full Scribe range (v1, v1_experimental, v2, v2_medical, v2_realtime) and
  // TTS range (turbo_v2_5/flash_v2_5/multilingual_v2 through v3/v4/v4_turbo),
  // plus Gemini for both. The real-time voice overlay's WS can only stream a
  // "live" model — picking a "batch" one here is still allowed (useful for
  // comparing what's available), it just won't power live voice chat, since
  // the backend silently falls back to its default live model for anything
  // outside LIVE_MODEL_IDS (tts_factory.py) / the fixed live STT model per
  // provider. The amber ticker below makes that fallback visible instead of
  // silent.
  const sttProviders = Object.keys(options?.stt_models_by_provider || {});
  const sttModelsForProvider = options?.stt_models_by_provider?.[sttProvider] || [];
  const ttsProviders = Object.keys(options?.tts_models_by_provider || {});
  const ttsModelsForProvider = options?.tts_models_by_provider?.[ttsProvider] || [];
  const selectedSttModel = sttModelsForProvider.find((m) => m.model_id === sttModelId);
  const selectedTtsModel = ttsModelsForProvider.find((m) => m.model_id === ttsModel);

  const handleSttProviderChange = (next) => {
    setSttProvider(next);
    const models = options?.stt_models_by_provider?.[next] || [];
    const firstLive = models.find((m) => m.kind === "live");
    setSttModelId((firstLive || models[0])?.model_id || "");
  };

  const handleTtsProviderChange = (next) => {
    setTtsProvider(next);
    const models = options?.tts_models_by_provider?.[next] || [];
    const firstLive = models.find((m) => m.kind === "live");
    setTtsModel((firstLive || models[0])?.model_id || "");
  };

  if (loggedIn) {
    return (
      <div className="dev-playground-wrapper">
        <div className="dev-playground-toolbar">
          <div className="dev-playground-field">
            <label>Chat model</label>
            <select value={modelId} onChange={(e) => setModelId(e.target.value)}>
              {options?.chat_models?.map((m) => (
                <option key={m.model_id} value={m.model_id}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="dev-playground-field">
            <label>STT provider</label>
            <select value={sttProvider} onChange={(e) => handleSttProviderChange(e.target.value)}>
              {sttProviders.map((p) => (
                <option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>
              ))}
            </select>
          </div>
          <div className="dev-playground-field">
            <label>STT model</label>
            <select value={sttModelId} onChange={(e) => setSttModelId(e.target.value)}>
              {sttModelsForProvider.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.label}{m.kind === "batch" ? " (batch)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="dev-playground-field">
            <label>TTS provider</label>
            <select value={ttsProvider} onChange={(e) => handleTtsProviderChange(e.target.value)}>
              {ttsProviders.map((p) => (
                <option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>
              ))}
            </select>
          </div>
          <div className="dev-playground-field">
            <label>TTS model</label>
            <select value={ttsModel} onChange={(e) => setTtsModel(e.target.value)}>
              {ttsModelsForProvider.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.label}{m.kind === "batch" ? " (batch)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="dev-playground-field">
            <label>&nbsp;</label>
            <button type="button" onClick={() => setShowLatencyLab((v) => !v)}>
              {showLatencyLab ? "Hide voice lab" : "🎧 Try the voices"}
            </button>
          </div>
        </div>
        {(selectedSttModel?.kind === "batch" || selectedTtsModel?.kind === "batch") && (
          <p className="voice-lab-warning">
            ⚠ {selectedSttModel?.kind === "batch" && `STT model "${selectedSttModel.label}" is batch (non-streaming)`}
            {selectedSttModel?.kind === "batch" && selectedTtsModel?.kind === "batch" && " and "}
            {selectedTtsModel?.kind === "batch" && `TTS model "${selectedTtsModel.label}" is batch (non-streaming)`}
            {" "}— live voice chat can't stream it, so it'll fall back to the provider's default live model.
            Use the Voice Lab below to actually test batch models (record a clip, get a full transcript/clip back).
          </p>
        )}
        {showLatencyLab && (
          <LatencyLab
            sttModelsByProvider={options?.stt_models_by_provider}
            ttsModelsByProvider={options?.tts_models_by_provider}
          />
        )}
        <DeepInsightsChat />
      </div>
    );
  }

  return (
    <div className="dev-playground-page">
      <form className="dev-playground-card" onSubmit={handleSubmit}>
        <h2>DeepInsights — Model/Engine Playground</h2>
        {error && <p className="dev-playground-error">{error}</p>}
        <button type="submit" disabled={loading}>
          {loading ? "Loading…" : "Enter Playground"}
        </button>
      </form>
    </div>
  );
}

export default DevPlayground;
